import { ApiError, apiRequest } from "./client";
import { API_BASE_URL } from "./config";
import type { BillingDetailsDto, BillingPaymentDto, PaymentMethodDto, SaveBillingDetailsPayload } from "./types";

/** The user's saved billing details, or null when none exist yet (checkout requires them). */
export const getBillingDetails = async (token: string): Promise<BillingDetailsDto | null> => {
	// The backend strips null data from the envelope, so a missing record arrives as undefined.
	const details = await apiRequest<BillingDetailsDto | undefined>("/billing/billing-details", { token });

	return details ?? null;
};

/** Create or update the user's billing details (one record per user). */
export const saveBillingDetails = (payload: SaveBillingDetailsPayload, token: string): Promise<BillingDetailsDto> =>
	apiRequest<BillingDetailsDto>("/billing/billing-details", { method: "PUT", body: payload, token });

/** The user's subscription payments, newest first, for the billing history table. */
export const getBillingPayments = async (token: string): Promise<BillingPaymentDto[]> => {
	const payments = await apiRequest<BillingPaymentDto[] | undefined>("/billing/payments", { token });

	return payments ?? [];
};

/** The instrument that paid the latest successful charge, or null when there is none yet. */
export const getPaymentMethod = async (token: string): Promise<PaymentMethodDto | null> => {
	const method = await apiRequest<PaymentMethodDto | undefined>("/billing/payment-method", { token });

	return method ?? null;
};

/**
 * Downloads the GST invoice PDF for a payment and hands it to the browser as a file save. Uses a
 * raw fetch because the endpoint streams a PDF rather than the JSON envelope apiRequest expects.
 */
export const downloadInvoice = async (paymentId: string, token: string): Promise<void> => {
	let response: Response;
	try {
		response = await fetch(`${API_BASE_URL}/api/v1/billing/payments/${paymentId}/invoice`, {
			headers: { Authorization: `Bearer ${token}` },
			credentials: "include"
		});
	} catch {
		throw new ApiError("Unable to reach the server. Check your connection and try again.", 0);
	}

	if (!response.ok) {
		throw new ApiError("The invoice could not be downloaded. Please try again.", response.status);
	}

	// File name comes from the Content-Disposition header ("Invoice-TT-....pdf").
	const disposition = response.headers.get("Content-Disposition") ?? "";
	const fileNameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
	const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : `Invoice-${paymentId}.pdf`;

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};
